import {
  ChartLabel,
  DatetimeValueChartLabels,
  DatetimeValueChartLabelsProps
} from '@widgets/common-d3-chart'
import {Moment} from 'moment'
import {useCallback} from 'react'
import {useTranslation} from 'react-i18next'

import {STOCK_CARD_WIDGET_NAMESPACE} from '../consts'

import {useCurrentTime} from './hooks/useCurrentTime'
import {useStockDevelopmentChartConfig} from './StockDevelopmentChartConfigProvider'

export type StockDevelopmentChartDatetimeLabelsProps = Pick<
  DatetimeValueChartLabelsProps,
  'yLabelWidth' | 'yStepsFormatter' | 'children'
> & {listOfDays: Moment[]}

export const StockDevelopmentChartDatetimeLabels: React.FC<
  StockDevelopmentChartDatetimeLabelsProps
> = ({children, listOfDays, ...props}) => {
  const {t} = useTranslation(STOCK_CARD_WIDGET_NAMESPACE)

  const {timezoneId, verticalSteps, labelSteps, xScale, yScale, formatDatetime} =
    useStockDevelopmentChartConfig()

  const currentTime = useCurrentTime({timezoneId})

  const overrideXLabel = useCallback(
    (datetime: Moment) => {
      const startOfDay = datetime.clone().startOf('day')
      const startOfCurrentDay = currentTime.clone().startOf('day')
      const startOfChart = listOfDays[0].clone()

      const shouldShowNowLabel =
        startOfDay.isSame(startOfCurrentDay, 'day') && currentTime.isAfter(startOfChart)

      return (
        <ChartLabel
          label={shouldShowNowLabel ? t('stockDevelopmentChart.now') : formatDatetime(datetime)}
          sx={{
            left: xScale((shouldShowNowLabel ? currentTime : datetime).clone()),
            transform: 'translateX(-50%)'
          }}
        />
      )
    },
    [currentTime, listOfDays, xScale, t, formatDatetime]
  )

  return (
    <DatetimeValueChartLabels
      {...props}
      xSteps={labelSteps}
      ySteps={verticalSteps}
      xScale={xScale}
      yScale={yScale}
      yTitle={t('stockDevelopmentChart.stockTons')}
      overrideXLabel={overrideXLabel}
    >
      {children}
    </DatetimeValueChartLabels>
  )
}
